import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { components, contentfulUtils } from '@glowforge/ui-common';
import { Link } from 'react-isomorphic-render';
import { formatPrice } from 'app/util';
import { productPropType } from 'app/store/components/glowforge/product-selector';

import './comparison-table.less';

const { stringSubstitution } = contentfulUtils;
const { SVG } = components;

const COLUMN_TITLE_ID_TO_MODEL_MAP = {
  'comparison-chart-pro-title': 'pro',
  'comparison-chart-plus-title': 'plus',
  'comparison-chart-basic-title': 'basic',
};

const colValueFieldsPropTypes = {
  type: PropTypes.oneOf(['boolean', 'text']),
  selected: PropTypes.bool,
  text: PropTypes.string,
};

const rowFieldsPropTypes = {
  values: PropTypes.arrayOf(PropTypes.shape({ fields: PropTypes.shape(colValueFieldsPropTypes) })),
  title: PropTypes.string,
};

/**
 * The value of a cell inside the table
 */
const ComparisonValue = (props) => {
  if (_.get(props.colValue, 'type') === 'boolean' && _.get(props.colValue, 'selected')) {
    return <SVG id='checkmark' />;
  } else if (_.get(props.colValue, 'type') === 'text') {
    return <span>{_.get(props.colValue, 'text')}</span>;
  }

  return null;
};

ComparisonValue.propTypes = {
  colValue: PropTypes.shape(colValueFieldsPropTypes),
};

ComparisonValue.defaultProps = {
  colValue: null,
};

/**
 * A Row inside the comparison table
 */
const ComparisonRow = (props) => {
  const colValues = _.map(
    _.get(props.row, 'values'),
    (col, index) => (
      <td className='value' key={`col_${index}`}><ComparisonValue colValue={col.fields} /></td>
    ),
  );

  return (
    <tr className='ComparisonRow'>
      <td className='rowTitle'>
        {_.get(props.row, 'title')}
      </td>
      {colValues}
    </tr>
  );
};

ComparisonRow.propTypes = {
  row: PropTypes.shape(rowFieldsPropTypes).isRequired,
};

/**
 * The base Comparison Table
 */
const ComparisonTable = (props) => {
  const tableData = props.comparisonData;

  if (!tableData) {
    return <div className='ComparisonTable'><SVG className='spin' id='spinner' /></div>;
  }

  const colHeaders = _.map(
    _.get(tableData, 'columnNames'),
    (col, index) => {
      const machine = COLUMN_TITLE_ID_TO_MODEL_MAP[_.get(col, 'fields.id')];
      const text = _.get(col, 'fields.text');
      let title = text;
      if (machine === 'pro') {
        title = <span className='pro'>{text} <SVG className='badge' id='badge-icon-pro' /></span>;
      } else if (machine === 'plus') {
        title = <span className='pro'>{text}</span>;
      }
      return <th key={index}>{title}</th>;
    },
  );

  const rows = _.map(
    _.get(tableData, 'comparisons'),
    (row, index) => (<ComparisonRow key={`row_${index}`} row={row.fields} />),
  );

  const priceRows = _.map(
    _.get(tableData, 'columnNames'),
    (col, index) => {
      const machine = COLUMN_TITLE_ID_TO_MODEL_MAP[_.get(col, 'fields.id')];
      return (
        <td className='value' key={`col_price_${index}`}>
          {formatPrice(props.products[machine].price)}
        </td>
      );
    },
  );

  let callToActions;
  if (props.showCallToAction) {
    const ctaButtons = _.map(
      _.get(tableData, 'columnNames'),
      (col, index) => {
        const machine = COLUMN_TITLE_ID_TO_MODEL_MAP[_.get(col, 'fields.id')];
        return (
          <td className='value' key={`col_price_${index}`}>
            <Link className='button secondary' to={`/order?id=${props.products[machine].id}`}>
              {props.callToActionText}
            </Link>
          </td>
        );
      },
    );

    callToActions = (
      <tr className='ComparisonRow callToAction'>
        <td className='rowTitle' />
        {ctaButtons}
      </tr>
    );
  }

  return (
    <div className='ComparisonTable'>
      <h2 className='h3 mobileHeader'>{stringSubstitution(tableData.title, props.stringSubstitutions)}</h2>
      <table>
        <thead>
          <tr>
            <th>
              <h2 className='h3 inlineHeader'>{stringSubstitution(tableData.title, props.stringSubstitutions)}</h2>
            </th>
            {colHeaders}
          </tr>
        </thead>
        <tbody>
          {rows}
          <tr className='ComparisonRow'>
            <td className='rowTitle'>Price</td>
            {priceRows}
          </tr>
          {callToActions}
        </tbody>
      </table>
    </div>
  );
};

ComparisonTable.propTypes = {
  comparisonData: PropTypes.shape({
    columnNames: PropTypes.arrayOf(PropTypes.shape({
      fields: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.string,
      }),
    })),
    comparisons: PropTypes.array,
    title: PropTypes.string,
  }),
  showCallToAction: PropTypes.bool,
  callToActionText: PropTypes.string,
  products: PropTypes.objectOf(PropTypes.shape(productPropType)).isRequired,
  stringSubstitutions: PropTypes.arrayOf(PropTypes.object),
};

ComparisonTable.defaultProps = {
  comparisonData: null,
  showCallToAction: false,
  callToActionText: 'Buy Now',
  stringSubstitutions: [],
};

export default ComparisonTable;



// WEBPACK FOOTER //
// ./app/common/components/comparison-table/comparison-table.jsx